import {
  BriefcaseIcon,
  CpuIcon,
  FileTextIcon,
  LayersIcon,
  MessagesSquareIcon,
  PencilLineIcon,
  RotateCcwIcon,
  UserRoundIcon,
} from "lucide-react";

import { SadieSimulatorEditedChip } from "./SadieSimulatorEditedChip";
import {
  FIELD_CONTENT_CLASS,
  FIELD_ICON_CLASS,
  FIELD_TRIGGER_CLASS,
  SadieSimulatorField as SimulatorField,
} from "./SadieSimulatorField";
import { SadieSimulatorOpenerField } from "./SadieSimulatorOpenerField";
import { SadieSimulatorOpportunityItem } from "./SadieSimulatorOpportunityItem";
import { SadieSimulatorResetButton } from "./SadieSimulatorResetButton";
import { SadieSimulatorWorkerDigest } from "./SadieSimulatorWorkerDigest";
import {
  SadieSimulatorWorkerInitiatedFields,
  SadieSimulatorWorkerInitiatedSetup,
} from "./SadieSimulatorWorkerInitiatedFields";
import type { SadieSimulatorJobSearchSetup } from "./SadieSimulatorJobSearchFields";
import {
  SadieSimulatorPostPipelineFields,
  SadieSimulatorPostPipelineSetup,
} from "./SadieSimulatorPostPipelineFields";

import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { Button } from "@ui/button";
import { Label } from "@ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@ui/select";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@ui/sheet";
import { Switch } from "@ui/switch";
import { Textarea } from "@ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@ui/tooltip";

import {
  SadieSimulatorMatchedOpportunityFit,
  SadieSimulatorMatchedOpportunityOption,
  SadieSimulatorPersona,
  SadieSimulatorPromptPreset,
  SadieSimulatorSessionKind,
} from "@/types";

// Offered by the backend but not ready to run yet, so they show greyed out.
const UNAVAILABLE_MODEL_PREFIXES = ["google/"];

const isModelUnavailable = (model: string) =>
  UNAVAILABLE_MODEL_PREFIXES.some((prefix) => model.startsWith(prefix));

// Every session kind the agent runs.
const SESSION_KIND_OPTIONS: {
  value: SadieSimulatorSessionKind;
  label: string;
}[] = [
  { value: "INTRO_LEARN", label: "Session 1: Cold Intro" },
  { value: "PROACTIVE_PULSE", label: "Session 2: Proactive Pulse" },
  { value: "WORKER_INITIATED", label: "Session 3: Worker Texts In" },
  { value: "POST_PIPELINE", label: "Session 4: Check-in" },
];

export const SadieSimulatorControls = ({
  chunked,
  defaultOpenerInstructions,
  disabled,
  instructions,
  instructionsDirty,
  isFitError,
  isFitPending,
  isLiveFit,
  isOpenerDirty,
  liveFits,
  matchedOpportunities,
  model,
  models,
  onChunkedChange,
  onInstructionsChange,
  onMatchedOpportunityChange,
  onModelChange,
  onOpenerInstructionsChange,
  onPersonaChange,
  onPresetChange,
  onResetInstructions,
  onResetOpenerInstructions,
  onRetryFit,
  onSessionKindChange,
  openerInstructions,
  personas,
  presets,
  selectedMatchedOpportunity,
  selectedPersona,
  selectedPreset,
  sessionKind,
  jobSearchSetup,
  postPipelineSetup,
  workerInitiatedSetup,
}: {
  chunked: boolean;
  defaultOpenerInstructions: string;
  disabled: boolean;
  instructions: string;
  instructionsDirty: boolean;
  isFitError: boolean;
  isFitPending: boolean;
  // A handed-off digest is in play, so the canned fit tags do not apply and
  // only the judge's read (liveFits) may be shown.
  isLiveFit: boolean;
  isOpenerDirty: boolean;
  liveFits?: Record<string, SadieSimulatorMatchedOpportunityFit>;
  matchedOpportunities: SadieSimulatorMatchedOpportunityOption[];
  model: string;
  models: string[];
  onChunkedChange: (value: boolean) => void;
  onInstructionsChange: (value: string) => void;
  onMatchedOpportunityChange: (key: string) => void;
  onModelChange: (value: string) => void;
  onOpenerInstructionsChange: (value: string) => void;
  onPersonaChange: (key: string) => void;
  onPresetChange: (key: string) => void;
  onResetInstructions: () => void;
  onResetOpenerInstructions: () => void;
  onRetryFit: () => void;
  onSessionKindChange: (kind: SadieSimulatorSessionKind) => void;
  openerInstructions: string;
  personas: SadieSimulatorPersona[];
  presets: SadieSimulatorPromptPreset[];
  selectedMatchedOpportunity: string;
  selectedPersona: string;
  selectedPreset: string;
  sessionKind: SadieSimulatorSessionKind;
  jobSearchSetup: SadieSimulatorJobSearchSetup;
  postPipelineSetup: SadieSimulatorPostPipelineSetup;
  workerInitiatedSetup: SadieSimulatorWorkerInitiatedSetup;
}) => {
  /* Helpers */
  const persona = personas.find((p) => p.key === selectedPersona);
  const fitFor = (
    option: SadieSimulatorMatchedOpportunityOption,
  ): SadieSimulatorMatchedOpportunityFit | undefined =>
    isLiveFit ? liveFits?.[option.key] : option.fitByPersona[selectedPersona];
  const selectedOption = matchedOpportunities.find(
    (m) => m.key === selectedMatchedOpportunity,
  );
  const selectedFit = selectedOption ? fitFor(selectedOption) : undefined;

  return (
    <Column className="gap-5">
      <Column className="gap-3">
        <SimulatorField icon={<CpuIcon />} id="sadie-sim-model" label="Model">
          <Select
            disabled={disabled}
            onValueChange={onModelChange}
            value={model}
          >
            <SelectTrigger className={FIELD_TRIGGER_CLASS} id="sadie-sim-model">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className={FIELD_CONTENT_CLASS}>
              {models.map((m) => (
                <SelectItem disabled={isModelUnavailable(m)} key={m} value={m}>
                  {m}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SimulatorField>

        <SimulatorField
          icon={<LayersIcon />}
          id="sadie-sim-session"
          label="Session"
        >
          <Select
            disabled={disabled}
            onValueChange={onSessionKindChange}
            value={sessionKind}
          >
            <SelectTrigger
              className={FIELD_TRIGGER_CLASS}
              id="sadie-sim-session"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className={FIELD_CONTENT_CLASS}>
              {SESSION_KIND_OPTIONS.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SimulatorField>

        <SimulatorField
          icon={<UserRoundIcon />}
          id="sadie-sim-persona"
          label="Worker"
        >
          <Select
            disabled={disabled}
            onValueChange={onPersonaChange}
            value={selectedPersona}
          >
            <SelectTrigger
              className={FIELD_TRIGGER_CLASS}
              id="sadie-sim-persona"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className={FIELD_CONTENT_CLASS}>
              {personas.map((p) => (
                <SelectItem key={p.key} value={p.key}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SimulatorField>

        {persona?.digest ? (
          <SadieSimulatorWorkerDigest
            digest={persona.digest}
            intentLevel={persona.intentLevel}
          />
        ) : null}

        {sessionKind === "WORKER_INITIATED" ? (
          <SadieSimulatorWorkerInitiatedFields
            disabled={disabled}
            fitFor={fitFor}
            matchedOpportunities={matchedOpportunities}
            jobSearchSetup={jobSearchSetup}
            setup={workerInitiatedSetup}
          />
        ) : null}

        {sessionKind === "POST_PIPELINE" ? (
          <SadieSimulatorPostPipelineFields
            disabled={disabled}
            fitFor={fitFor}
            jobSearchSetup={jobSearchSetup}
            matchedOpportunities={matchedOpportunities}
            setup={postPipelineSetup}
          />
        ) : null}

        {sessionKind === "PROACTIVE_PULSE" ? (
          <SimulatorField
            icon={<BriefcaseIcon />}
            id="sadie-sim-matched-opportunity"
            label="Matched opportunity"
          >
            <Select
              disabled={disabled}
              onValueChange={onMatchedOpportunityChange}
              value={selectedMatchedOpportunity}
            >
              <SelectTrigger
                className={FIELD_TRIGGER_CLASS}
                id="sadie-sim-matched-opportunity"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className={FIELD_CONTENT_CLASS}>
                {matchedOpportunities.map((m) => (
                  <SadieSimulatorOpportunityItem
                    fit={fitFor(m)}
                    isFitPending={isFitPending}
                    key={m.key}
                    option={m}
                  />
                ))}
              </SelectContent>
            </Select>
            {selectedFit ? (
              <Text className="text-xs text-slate-500">{selectedFit.note}</Text>
            ) : null}
            {isLiveFit && isFitError ? (
              <Row className="items-center gap-1">
                <Text className="text-xs text-slate-500">
                  Could not read the fit for this digest.
                </Text>
                <Button
                  className="h-auto p-0 text-xs"
                  onClick={onRetryFit}
                  size="sm"
                  variant="link"
                >
                  Retry
                </Button>
              </Row>
            ) : null}
          </SimulatorField>
        ) : null}

        <SimulatorField
          icon={<FileTextIcon />}
          id="sadie-sim-preset"
          label="Preset"
        >
          <Select
            disabled={disabled}
            onValueChange={onPresetChange}
            value={selectedPreset}
          >
            <SelectTrigger
              className={FIELD_TRIGGER_CLASS}
              id="sadie-sim-preset"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className={FIELD_CONTENT_CLASS}>
              {presets.map((p) => (
                <SelectItem key={p.key} value={p.key}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SimulatorField>

        <Row className="items-center justify-between gap-3 py-1">
          <Row className="items-center gap-2">
            <MessagesSquareIcon className={FIELD_ICON_CLASS} />
            <Column className="gap-0">
              <Label
                className="text-sm font-medium text-slate-700"
                htmlFor="sadie-sim-chunked"
              >
                Send in pieces
              </Label>
              <Text className="text-xs text-slate-500">
                Split a reply into separate texts
              </Text>
            </Column>
          </Row>
          <Switch
            checked={chunked}
            disabled={disabled}
            id="sadie-sim-chunked"
            onCheckedChange={onChunkedChange}
          />
        </Row>
      </Column>

      <div className="border-t border-slate-200" />

      <Column className="gap-1.5">
        {sessionKind === "PROACTIVE_PULSE" ? (
          <SadieSimulatorOpenerField
            defaultValue={defaultOpenerInstructions}
            disabled={disabled}
            isDirty={isOpenerDirty}
            onChange={onOpenerInstructionsChange}
            onReset={onResetOpenerInstructions}
            value={openerInstructions}
          />
        ) : null}
        <Row className="h-7 items-center justify-between">
          <Row className="items-center gap-2">
            <FileTextIcon className={FIELD_ICON_CLASS} />
            <Text className="text-sm font-medium text-slate-700">Prompt</Text>
            {instructionsDirty ? <SadieSimulatorEditedChip /> : null}
          </Row>
          <Row className="items-center gap-0.5">
            <SadieSimulatorResetButton
              disabled={disabled || !instructionsDirty}
              onClick={onResetInstructions}
            />
            <Sheet>
              <Tooltip>
                <TooltipTrigger asChild>
                  <SheetTrigger asChild>
                    <Button
                      aria-label="Edit prompt"
                      className="h-7 w-7 text-slate-500"
                      disabled={disabled}
                      size="icon"
                      variant="ghost"
                    >
                      <PencilLineIcon className="h-3.5 w-3.5" />
                    </Button>
                  </SheetTrigger>
                </TooltipTrigger>
                <TooltipContent>Edit prompt</TooltipContent>
              </Tooltip>
              <SheetContent className="flex w-full flex-col gap-4 sm:w-[760px] sm:max-w-[760px]">
                <SheetHeader>
                  <Row className="items-center gap-2">
                    <SheetTitle>Prompt</SheetTitle>
                    {instructionsDirty ? <SadieSimulatorEditedChip /> : null}
                  </Row>
                  <SheetDescription>
                    Exactly what the agent runs. Edits apply to the next turn
                    and are replaced when the worker or preset changes.
                  </SheetDescription>
                </SheetHeader>
                <Textarea
                  className="min-h-0 flex-1 resize-none rounded-lg p-3 font-mono text-xs leading-relaxed text-slate-800 md:text-xs"
                  id="sadie-sim-instructions"
                  onChange={(e) => {
                    onInstructionsChange(e.target.value);
                  }}
                  spellCheck={false}
                  value={instructions}
                />
                <SheetFooter className="items-center sm:justify-between">
                  <Text className="text-xs text-slate-500">
                    {instructions.length.toLocaleString()} characters
                  </Text>
                  <Row className="items-center gap-2">
                    <Button
                      disabled={!instructionsDirty}
                      onClick={onResetInstructions}
                      size="sm"
                      variant="ghost"
                    >
                      <RotateCcwIcon className="h-3.5 w-3.5" />
                      Reset to default
                    </Button>
                    <SheetClose asChild>
                      <Button size="sm">Done</Button>
                    </SheetClose>
                  </Row>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </Row>
        </Row>
        <Text className="text-xs text-slate-500">
          {instructions.length.toLocaleString()} characters. Exactly what the
          agent runs.
        </Text>
      </Column>
    </Column>
  );
};
